'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useIsReactNativeWebView } from '@/lib/react-native/contexts/react-native/ReactNativeProvider.client'
import type { WebViewIntent } from '@/lib/react-native/services/react-native.intent'
import type { TrackingGaEvent } from '@/lib/tracking/services/tracking.eventTypes'
import { Card } from '@/ui/cards/card/Card'
import { Link } from '@/ui/links/link/Link.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import { Notification } from '@/ui/notification/Notification'
import { BaseSkeleton } from '@/ui/skeleton/BaseSkeleton'
import type { Image } from '@gql-generated/graphql'
import { cva } from 'class-variance-authority'
import {
  NAVIGATION_CARD_PICTURE_CLASSNAME,
  NAVIGATION_CARD_PICTURE_MODE,
} from './NavigationCard.constants'

export type NavigationCardSize = 'lg' | 'md' | 'sm'

const navigationCardCva = cva('items-center gap-1 font-bold leading-light', {
  variants: {
    layout: {
      lg: 'min-h-[5.25rem] w-full max-w-[28rem] flex-col justify-center',
      md: 'w-full',
      sm: 'size-15 shrink-0 flex-col justify-center',
      media: 'h-[7.75rem] w-full gap-2 desktop:gap-1',
    },
  },
})

const navigationCardLabelCva = cva('w-full overflow-hidden', {
  variants: {
    layout: {
      lg: 'line-clamp-2 text-center uppercase',
      md: 'truncate uppercase',
      sm: 'line-clamp-2 text-center uppercase',
      media: 'flex flex-col justify-center break-words',
    },
  },
})

type NavigationCardBaseProps = UiComponentProps<
  WithPayloadBlockId<{
    href: string
    title?: string
    hasNotification?: boolean
    onPress?: () => void
    trackingData?: TrackingGaEvent
    opensInNewTab?: boolean
    intent?: WebViewIntent
    children: React.ReactNode
  }>
>

export type NavigationCardProps = NavigationCardBaseProps &
  (
    | {
        size: NavigationCardSize
        icon: React.ReactElement
        image?: never
        imageComponent?: never
      }
    | {
        size?: never
        icon?: never
        image: Image
        imageComponent?: never
      }
    | {
        size?: never
        icon?: never
        image?: never
        imageComponent: React.ReactElement
      }
  )

export function NavigationCard(props: NavigationCardProps) {
  const isInWebView = useIsReactNativeWebView()

  // ⚠️ Temporary: hide buttons without intent in the WebView until their specification is ready,
  // to avoid displaying TODO alerts in production
  if (isInWebView && !props.intent && process.env.NODE_ENV === 'production') {
    return null
  }

  const {
    children,
    hasNotification = false,
    className,
    href,
    title,
    onPress,
    trackingData,
    opensInNewTab,
    intent,
    id,
    'data-testid': dataTestId,
    'aria-label': ariaLabel,
    'data-payload-block-id': dataPayloadBlockId,
  } = props

  const layout = props.icon ? props.size : 'media'
  const image = 'image' in props ? props.image : undefined
  const imageComponent = 'imageComponent' in props ? props.imageComponent : undefined

  return (
    <Link
      variant="free"
      underlined="none"
      href={href}
      title={title}
      className={className}
      onPress={onPress}
      trackingData={trackingData}
      opensInNewTab={opensInNewTab}
      intent={intent}
      id={id}
      aria-label={ariaLabel}
      data-testid={dataTestId ?? 'navigation-card'}
      data-payload-block-id={dataPayloadBlockId}
    >
      <Card radius="sm" skin="body-alt" className={navigationCardCva({ layout })}>
        <div className="relative shrink-0">
          {props.icon && (
            <>
              {hasNotification && (
                <Notification className="absolute -right-[0.375rem] -top-[0.375rem]" />
              )}
              <div className="m-auto size-3" aria-hidden>
                {props.icon}
              </div>
            </>
          )}
          {(image || imageComponent) && (
            <div className="flex justify-center">
              {image ? (
                <Picture
                  className={NAVIGATION_CARD_PICTURE_CLASSNAME}
                  image={image}
                  mode={NAVIGATION_CARD_PICTURE_MODE}
                />
              ) : (
                imageComponent
              )}
            </div>
          )}
        </div>
        <div className={navigationCardLabelCva({ layout })}>{children}</div>
      </Card>
    </Link>
  )
}

export type NavigationCardSkeletonProps = UiComponentProps<{
  size: NavigationCardSize
}>

export function NavigationCardSkeleton({
  size,
  className,
  id,
  'data-testid': dataTestId,
}: NavigationCardSkeletonProps) {
  return (
    <Card
      id={id}
      radius="sm"
      skin="body-alt"
      aria-hidden
      data-testid={dataTestId ?? 'navigation-card-skeleton'}
      className={twMerge(className, navigationCardCva({ layout: size }))}
    >
      <BaseSkeleton className="size-3 shrink-0 rounded-full" />
      <BaseSkeleton
        className={twMerge(
          'h-[0.875rem] shrink-0',
          size === 'md' && 'w-12',
          size === 'lg' && 'w-14',
          size === 'sm' && 'w-9',
        )}
      />
    </Card>
  )
}
